import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  AlarmClock,
  ArrowLeft,
  AtSign,
  Bell,
  BellOff,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Download,
  FileText,
  FolderKanban,
  Globe2,
  Inbox,
  Info,
  Mail,
  MessageSquareText,
  MoreHorizontal,
  Pencil,
  Pin,
  Play,
  Plus,
  Reply,
  Settings2,
  Sparkles,
  StickyNote,
  Text,
  Trash2,
  Users,
} from 'lucide-react';
import { NOTIFICATION_TONES, type NotificationTone, type ProjectCover, type ProjectFile, type ProjectNote, type ProjectRuleKind, type ProjectSummary, type ThreadSummary } from '@mailpilot/shared';
import { useMe } from '../hooks/useMe';
import { useMailboxes } from '../hooks/useMailboxes';
import { useCategoryMap } from '../hooks/useCategories';
import { useBlobUrl } from '../hooks/useBlobUrl';
import { useUpdateThread } from '../hooks/useThreads';
import { useCurrentWorkspace, useWorkspaceDetail } from '../hooks/useWorkspace';
import {
  useAddProjectNote,
  useCreateProject,
  useDeleteProject,
  useDeleteProjectNote,
  useProject,
  useProjectFiles,
  useProjectNotes,
  useProjectPrefs,
  useProjects,
  useProjectThreads,
  useSetProjectMembers,
  useSetProjectRules,
  useUpdateProject,
  uploadProjectCover,
} from '../hooks/useProjects';
import { downloadFile } from '../lib/api';
import { bytes, displayName, fullDate, listTime, relativeTime } from '../lib/format';
import { playTone, unlockAudio } from '../lib/sound';
import { Avatar } from '../components/ui/Avatar';
import { Menu } from '../components/ui/Overlay';
import { Button, Card, CardBody, CardHeader, EmptyState, ErrorState, Field, FolderCard, FolderRow, HeroStat, Input, RoundButton, Skeleton, Switch, TabBar, Timeline, TimelineItem, useConfirm, useToast } from '../components/ui';
import { CoverPicker, MemberPicker, NewProjectModal, ProjectCard, ProjectCoverView, RulesEditor } from '../components/projects/ProjectParts';
import { ThreadRow } from '../components/mail/ThreadRow';
import { ThreadView } from '../components/mail/ThreadView';
import { useBreakpoint } from '../hooks/useBrowser';

function OrgOnly() {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <EmptyState icon={<FolderKanban className="h-6 w-6" />} title="Projects are for organizations" description="Switch to an organization (top left) to see its projects, or create one to start sharing mail with a team." />
    </div>
  );
}

// ------------------------------------------------------------------ all projects

export function ProjectsPage() {
  const { isOrg } = useCurrentWorkspace();
  const me = useMe().data;
  const list = useProjects(isOrg);
  const people = useWorkspaceDetail(isOrg).data?.members ?? [];
  const create = useCreateProject();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!isOrg) return <OrgOnly />;
  const projects = list.data?.projects ?? [];
  const totalNew = projects.reduce((n, p) => n + p.unreadCount, 0);
  const totalConversations = projects.reduce((n, p) => n + p.conversationCount, 0);
  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <div className="mx-auto max-w-6xl px-4 py-6 md:px-8">
        <header className="mb-7 flex flex-wrap items-end justify-between gap-5">
          <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-ink">Projects</h1>
              <p className="mt-1 text-[0.875rem] text-muted">Every client, deal or job's emails in one folder, followed up by the right people.</p>
            </div>
            {projects.length > 0 && (
              <div className="flex gap-8">
                <HeroStat label="Projects" value={projects.length} />
                <HeroStat label="Conversations" value={totalConversations} />
                <HeroStat label="New to you" value={totalNew} caption={totalNew ? 'Across all projects' : 'All caught up'} />
              </div>
            )}
          </div>
          {list.data?.canCreate && (
            <Button variant="primary" size="lg" className="!rounded-full" icon={<Plus className="h-4 w-4" />} onClick={() => setOpen(true)}>
              New project
            </Button>
          )}
        </header>
        {list.isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-72 rounded-3xl" />
            ))}
          </div>
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : projects.length === 0 ? (
          <Card>
            <EmptyState
              className="py-16"
              icon={<FolderKanban className="h-6 w-6" />}
              title={list.data?.canCreate ? 'No projects yet' : 'You’re not in any projects yet'}
              description={list.data?.canCreate ? 'Create one, point a client’s senders at it, and choose who follows up.' : 'When an owner or admin adds you to a project, it appears here.'}
              actions={list.data?.canCreate ? <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setOpen(true)}>New project</Button> : undefined}
            />
          </Card>
        ) : (
          <div className="grid gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <ProjectCard key={p.id} p={p} />
            ))}
          </div>
        )}
      </div>
      {open && me && (
        <NewProjectModal
          open
          onClose={() => setOpen(false)}
          people={people}
          meId={me.user.id}
          busy={create.isPending}
          error={create.error}
          onCreate={(b) =>
            create.mutate(b, {
              onSuccess: (r) => {
                setOpen(false);
                navigate(`/projects/${r.project.id}`);
              },
            })
          }
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ one project

type Tab = 'conversations' | 'activity' | 'senders' | 'members' | 'settings';
const TONE_LABEL = Object.fromEntries(NOTIFICATION_TONES.map((t) => [t.id, t.label])) as Record<NotificationTone, string>;

const relDay = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const diff = Math.round((new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};
const pillFor = (iso: string) => {
  const r = relDay(iso);
  return { label: r, tone: r === 'Today' ? 'mp-pill-lime' : r === 'Yesterday' ? 'mp-pill-lavender' : 'mp-pill-neutral' };
};

export function ProjectPage() {
  const { projectId, threadId } = useParams();
  const navigate = useNavigate();
  const { isOrg } = useCurrentWorkspace();
  const q = useProject(projectId);
  const threads = useProjectThreads(projectId);
  const prefs = useProjectPrefs(projectId ?? '');
  const [tab, setTab] = useState<Tab>('conversations');
  const bp = useBreakpoint();
  const list = useMemo(() => threads.data?.pages.flatMap((pg) => pg.threads) ?? [], [threads.data]);
  const mailboxList = useMailboxes().data?.mailboxes;
  const mailboxMap = useMemo(() => new Map((mailboxList ?? []).map((m) => [m.id, m])), [mailboxList]);

  // Hero stats and the 14-day strip come from the loaded conversations.
  const stats = useMemo(() => {
    const needsReply = list.filter((t) => t.awaitingReplyFrom === 'me').length;
    const followUps = list.filter((t) => t.followUp?.due).length;
    return { needsReply, followUps };
  }, [list]);

  if (!isOrg) return <OrgOnly />;
  if (q.isLoading) return <div className="p-8"><Skeleton className="h-40 rounded-3xl" /></div>;
  if (q.isError || !q.data) return <div className="p-8"><ErrorState error={q.error} title="Couldn't open this project" /></div>;
  const p = q.data.project;
  const notifyOn = !!p.me?.notify;
  const tabs: { id: Tab; label: string; icon: ReactNode; count?: number }[] = [
    { id: 'conversations', label: 'Conversations', icon: <Inbox />, count: p.unreadCount },
    { id: 'activity', label: 'Activity', icon: <MessageSquareText /> },
    { id: 'senders', label: 'Senders & rules', icon: <AtSign /> },
    { id: 'members', label: 'Members', icon: <Users />, count: p.members.length },
    { id: 'settings', label: 'Settings', icon: <Settings2 /> },
  ];
  const reading = !!threadId;
  const mobile = bp === 'mobile';

  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <div className="mx-auto max-w-[1400px] px-4 pb-8 pt-5 md:px-6">
        {/* ---- header: back, name, tag, round actions */}
        <header className="flex flex-wrap items-center gap-3">
          <Link to="/projects" aria-label="All projects" className="mp-round-btn">
            <ArrowLeft />
          </Link>
          <h1 className="truncate text-2xl font-semibold tracking-tight text-ink">{p.name}</h1>
          <span className="mp-pill mp-pill-mint">{p.rules[0]?.value ?? 'Project'}{p.rules.length > 1 ? ` +${p.rules.length - 1}` : ''}</span>
          <div className="ml-auto flex items-center gap-2">
            {p.me && (
              <RoundButton lg label={notifyOn ? 'Notifications on — click to mute' : 'Muted — click to notify me'} active={notifyOn} icon={notifyOn ? <Bell /> : <BellOff />} onClick={() => prefs.mutate({ notify: !notifyOn })} />
            )}
            {p.me && (
              <Menu
                label="Notification sound"
                align="right"
                width={220}
                header={<p className="px-3 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">Sound for this project</p>}
                trigger={(t) => (
                  <button type="button" {...t} className="flex h-10 items-center gap-2 rounded-full border border-line/80 bg-surface/90 pl-3 pr-3.5 text-[0.8125rem] font-medium text-ink transition-colors hover:bg-raised">
                    <Play className="h-3.5 w-3.5 text-muted" aria-hidden />
                    {TONE_LABEL[p.me!.tone]}
                  </button>
                )}
                items={NOTIFICATION_TONES.map((t) => ({
                  key: t.id,
                  label: t.label,
                  active: p.me?.tone === t.id,
                  onSelect: () => {
                    prefs.mutate({ tone: t.id });
                    void unlockAudio().then(() => playTone(t.id, 0.5));
                  },
                }))}
              />
            )}
            <RoundButton lg label="Project settings" icon={<Settings2 />} onClick={() => setTab('settings')} />
          </div>
        </header>

        {/* ---- hero: big number, stats, pills, date */}
        <section className="mt-5 flex flex-wrap items-end justify-between gap-x-10 gap-y-5">
          <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
            <HeroStat label="Conversations" value={p.conversationCount} size="lg" />
            <div className="flex flex-wrap items-center gap-2 pb-2.5">
              <span className={clsx('mp-pill !h-8 !px-3.5 !text-[0.8125rem]', p.unreadCount ? 'mp-pill-lime' : 'mp-pill-neutral')}>{p.unreadCount} new to you</span>
              <span className={clsx('mp-pill !h-8 !px-3.5 !text-[0.8125rem]', stats.needsReply ? 'mp-pill-yellow' : 'mp-pill-neutral')}>{stats.needsReply} waiting on you</span>
              <span className={clsx('mp-pill !h-8 !px-3.5 !text-[0.8125rem]', stats.followUps ? 'mp-pill-salmon' : 'mp-pill-neutral')}>
                <AlarmClock className="h-3.5 w-3.5" aria-hidden /> {stats.followUps} follow-up{stats.followUps === 1 ? '' : 's'} due
              </span>
            </div>
          </div>
          <p className="flex items-center gap-1.5 pb-1.5 text-[0.8125rem] text-muted">
            <CalendarDays className="h-4 w-4" aria-hidden /> {fullDate(new Date().toISOString())}
          </p>
        </section>

        {/* ---- body: details | tabs | files */}
        <div className={clsx('mt-6 grid gap-6', reading ? 'lg:grid-cols-[272px_minmax(0,1fr)]' : 'lg:grid-cols-[272px_minmax(0,1fr)] xl:grid-cols-[272px_minmax(0,1fr)_256px]')}>
          <aside className={clsx('space-y-6', mobile && reading && 'hidden')}>
            <FolderCard
              tone="yellow"
              title="Details"
              icon={<Info />}
              actions={p.canManage ? <RoundButton label="Edit details" icon={<Pencil />} onClick={() => setTab('settings')} /> : undefined}
            >
              <div className="space-y-3.5">
                <FolderRow icon={<FolderKanban />} label="Project" value={p.name} />
                {p.description && <FolderRow icon={<Text />} label="About" value={<span className="whitespace-normal text-[0.875rem] font-medium leading-snug">{p.description}</span>} />}
                <FolderRow icon={<AtSign />} label={`Mail from${p.rules.length > 2 ? ` (${p.rules.length})` : ''}`} value={p.rules.length ? p.rules.slice(0, 2).map((r) => r.value).join(', ') + (p.rules.length > 2 ? '…' : '') : 'No rules yet'} />
                <FolderRow icon={<CalendarDays />} label="Created" value={fullDate(p.createdAt)} />
                <div className="mp-folder-line flex gap-1.5 border-t pt-3">
                  <RoundButton label="Conversations" icon={<Inbox />} onClick={() => setTab('conversations')} className="!border-transparent !bg-black/10 !text-current hover:!bg-black/15" />
                  <RoundButton label="Senders & rules" icon={<Globe2 />} onClick={() => setTab('senders')} className="!border-transparent !bg-black/10 !text-current hover:!bg-black/15" />
                  <RoundButton label="Activity" icon={<MessageSquareText />} onClick={() => setTab('activity')} className="!border-transparent !bg-black/10 !text-current hover:!bg-black/15" />
                </div>
              </div>
            </FolderCard>
            <FolderCard
              tone="lavender"
              title="Members"
              icon={<Users />}
              actions={p.canManage ? <RoundButton label="Edit members" icon={<Pencil />} onClick={() => setTab('members')} /> : undefined}
            >
              <ul className="space-y-2.5">
                {p.members.map((m) => (
                  <li key={m.userId} className="flex items-center gap-2.5">
                    <Avatar name={m.name} email={m.email} size={30} ring="rgb(var(--vivid-lavender))" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[0.875rem] font-semibold leading-tight">{m.name}</span>
                      <span className="mp-folder-muted block truncate text-xs">{m.role === 'manager' ? 'Manager' : 'Member'}</span>
                    </span>
                  </li>
                ))}
              </ul>
              {p.me && (
                <div className="mp-folder-line mt-3.5 flex items-center justify-between border-t pt-3 text-xs">
                  <span className="mp-folder-muted">Your sound</span>
                  <span className="font-semibold">{TONE_LABEL[p.me!.tone]}</span>
                </div>
              )}
            </FolderCard>
          </aside>

          <section className="min-w-0">
            {reading ? (
              <div className="mp-panel h-[calc(100dvh-140px)] min-h-[560px] overflow-hidden">
                <ThreadView threadId={threadId!} mailboxes={mailboxMap} onBack={() => navigate(`/projects/${p.id}`)} detailsOpen={false} onToggleDetails={() => undefined} details={null} />
              </div>
            ) : (
              <>
                <TabBar label="Project sections" value={tab} onChange={setTab} tabs={tabs} />
                <div className={clsx('mp-panel min-h-[420px]', tab === tabs[0].id && 'mp-tabpanel')}>
                  {tab === 'conversations' && <ProjectConversations p={p} threads={list} loading={threads.isLoading} hasMore={!!threads.hasNextPage} loadingMore={threads.isFetchingNextPage} onMore={() => void threads.fetchNextPage()} />}
                  {tab === 'activity' && <ProjectActivity p={p} threads={list} />}
                  {tab === 'senders' && <ProjectSenders p={p} />}
                  {tab === 'members' && <ProjectMembers p={p} />}
                  {tab === 'settings' && <ProjectSettings p={p} />}
                </div>
              </>
            )}
          </section>

          {!reading && (
            <aside className={clsx('hidden xl:block')}>
              <ProjectFiles p={p} />
            </aside>
          )}
        </div>
        {!reading && (
          <div className="mt-6 xl:hidden">
            <ProjectFiles p={p} />
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ conversations (grouped timeline)

function ConversationCard({ t, p, mailboxName }: { t: ThreadSummary; p: ProjectSummary; mailboxName?: string }) {
  const navigate = useNavigate();
  const pill = pillFor(t.lastMessageAt);
  const sender = t.lastSender ?? t.participants[0] ?? null;
  const waitingOnMe = t.awaitingReplyFrom === 'me';
  return (
    <button
      type="button"
      onClick={() => navigate(`/projects/${p.id}/${t.id}`)}
      className="group flex w-full items-start gap-3 rounded-2xl border border-line/70 bg-surface px-4 py-3 text-left transition-[transform,box-shadow,border-color] hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-card dark:bg-raised/60"
    >
      <span className={clsx('mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border', waitingOnMe ? 'border-vivid-yellow-ink/40 bg-vivid-yellow text-vivid-yellow-ink' : t.awaitingReplyFrom === 'them' ? 'border-transparent bg-vivid-lime text-vivid-lime-ink' : 'border-line bg-transparent text-transparent')} aria-hidden>
        {t.awaitingReplyFrom === 'them' ? <CheckCircle2 className="h-3.5 w-3.5" /> : waitingOnMe ? <Clock3 className="h-3.5 w-3.5" /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className={clsx('min-w-0 flex-1 truncate text-[0.9375rem] leading-snug text-ink', t.unread ? 'font-semibold' : 'font-medium')}>{t.subject || '(no subject)'}</span>
          <span className={clsx('mp-pill shrink-0', pill.tone)}>{pill.label}</span>
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
          <span className="truncate">{sender ? displayName(sender) : 'Unknown sender'}</span>
          {mailboxName && <span className="flex items-center gap-1"><Mail className="h-3 w-3" aria-hidden /> {mailboxName}</span>}
          {t.assignedTo && (
            <span className="flex items-center gap-1">
              <Avatar name={t.assignedTo.name} email={t.assignedTo.email} size={16} /> {(t.assignedTo.name || t.assignedTo.email).split(' ')[0]}
            </span>
          )}
          {t.followUp?.due && <span className="mp-pill mp-pill-salmon !h-5"><AlarmClock className="h-3 w-3" aria-hidden /> No reply</span>}
          <span className="ml-auto text-subtle">{listTime(t.lastMessageAt)}</span>
        </span>
      </span>
    </button>
  );
}

function ProjectConversations({ p, threads, loading, hasMore, loadingMore, onMore }: { p: ProjectSummary; threads: ThreadSummary[]; loading: boolean; hasMore: boolean; loadingMore: boolean; onMore: () => void }) {
  const mbs = useMailboxes().data?.mailboxes ?? [];
  const nameOf = (id: string) => mbs.find((m) => m.id === id)?.displayName;
  const groups = [
    { id: 'me', title: 'Waiting on you', icon: <Clock3 />, tone: 'yellow' as const, items: threads.filter((t) => t.awaitingReplyFrom === 'me') },
    { id: 'them', title: 'Waiting on them', icon: <Reply />, tone: 'lime' as const, items: threads.filter((t) => t.awaitingReplyFrom === 'them') },
    { id: 'quiet', title: 'Quiet', icon: <CheckCircle2 />, tone: 'surface' as const, items: threads.filter((t) => t.awaitingReplyFrom !== 'me' && t.awaitingReplyFrom !== 'them') },
  ].filter((g) => g.items.length);
  if (loading) {
    return (
      <div className="space-y-3 p-5">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[72px] rounded-2xl" />
        ))}
      </div>
    );
  }
  if (!threads.length) {
    return <EmptyState className="py-16" icon={<Inbox className="h-6 w-6" />} title="No conversations yet" description="Add senders under “Senders & rules”, or add a conversation from your inbox with the folder button." />;
  }
  return (
    <div className="p-5 md:p-6">
      {groups.map((g) => (
        <section key={g.id} className="mb-2">
          <h3 className="mb-3 flex items-center gap-2 text-[0.9375rem] font-semibold text-ink">
            {g.title} <span className="mp-pill mp-pill-neutral !h-5">{g.items.length}</span>
          </h3>
          <Timeline>
            {g.items.map((t) => (
              <TimelineItem key={t.id} icon={g.icon} tone={g.tone} date={relDay(t.lastMessageAt) === 'Today' ? 'Today' : listTime(t.lastMessageAt)}>
                <ConversationCard t={t} p={p} mailboxName={nameOf(t.mailboxId)} />
              </TimelineItem>
            ))}
          </Timeline>
        </section>
      ))}
      {hasMore && (
        <div className="pt-2 text-center">
          <Button size="sm" loading={loadingMore} onClick={onMore}>Load more</Button>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ activity (notes + mail events) with "Take a note"

function ProjectActivity({ p, threads }: { p: ProjectSummary; threads: ThreadSummary[] }) {
  const notes = useProjectNotes(p.id);
  const add = useAddProjectNote(p.id);
  const del = useDeleteProjectNote(p.id);
  const me = useMe().data;
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (text.trim()) add.mutate(text.trim(), { onSuccess: () => setText('') });
  };
  type Ev = { key: string; at: string; kind: 'note' | 'mail' | 'reply'; note?: ProjectNote; thread?: ThreadSummary };
  const events: Ev[] = [
    ...(notes.data?.notes ?? []).map((n) => ({ key: `n${n.id}`, at: n.createdAt, kind: 'note' as const, note: n })),
    ...threads.map((t) => ({ key: `t${t.id}`, at: t.lastMessageAt, kind: t.awaitingReplyFrom === 'them' ? ('reply' as const) : ('mail' as const), thread: t })),
  ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  return (
    <div className="flex min-h-[420px] flex-col">
      <div className="flex-1 p-5 md:p-6">
        {events.length === 0 ? (
          <EmptyState className="py-12" icon={<MessageSquareText className="h-6 w-6" />} title="Nothing yet" description="Mail activity and your team's notes on this project will show up here." />
        ) : (
          <Timeline>
            {events.map((ev) =>
              ev.kind === 'note' && ev.note ? (
                <TimelineItem key={ev.key} icon={<StickyNote />} tone="lavender" date={relDay(ev.at) === 'Today' ? 'Today' : listTime(ev.at)}>
                  <div className="group rounded-2xl bg-vivid-yellow/70 px-4 py-3 text-vivid-yellow-ink dark:bg-vivid-yellow/80">
                    <div className="flex items-center gap-2 text-xs">
                      <Avatar name={ev.note.author?.name} email={ev.note.author?.email ?? ev.note.id} size={18} />
                      <span className="font-semibold">{ev.note.author?.name ?? 'Former member'}</span>
                      <span className="opacity-70" title={fullDate(ev.at)}>{relativeTime(ev.at)}</span>
                      {(ev.note.author?.id === me?.user.id || p.canManage) && (
                        <button type="button" aria-label="Delete note" onClick={() => del.mutate(ev.note!.id)} className="ml-auto rounded-md p-1 opacity-0 transition-opacity hover:bg-black/10 group-hover:opacity-100 focus:opacity-100">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="mt-1.5 whitespace-pre-wrap break-words text-[0.875rem] leading-relaxed">{ev.note.body}</p>
                  </div>
                </TimelineItem>
              ) : ev.thread ? (
                <TimelineItem key={ev.key} icon={ev.kind === 'reply' ? <Reply /> : <Mail />} tone={ev.kind === 'reply' ? 'lime' : 'sky'} date={relDay(ev.at) === 'Today' ? 'Today' : listTime(ev.at)}>
                  <button type="button" onClick={() => navigate(`/projects/${p.id}/${ev.thread!.id}`)} className="flex w-full items-center gap-3 rounded-2xl border border-line/70 bg-surface px-4 py-3 text-left transition-colors hover:border-primary/30 dark:bg-raised/60">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[0.875rem] font-medium text-ink">{ev.kind === 'reply' ? 'Replied · ' : 'New mail · '}{ev.thread.subject || '(no subject)'}</span>
                      <span className="block truncate text-xs text-muted">{ev.thread.lastSender ? displayName(ev.thread.lastSender) : ''}</span>
                    </span>
                    <span className={clsx('mp-pill', pillFor(ev.at).tone)}>{pillFor(ev.at).label}</span>
                  </button>
                </TimelineItem>
              ) : null,
            )}
          </Timeline>
        )}
      </div>
      <form onSubmit={submit} className="sticky bottom-0 flex items-center gap-2 border-t border-line/70 bg-surface/90 p-3 backdrop-blur md:px-5">
        <StickyNote className="ml-1 h-4 w-4 shrink-0 text-muted" aria-hidden />
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} placeholder="Take a note for the team…" aria-label="Take a note" className="h-10 min-w-0 flex-1 bg-transparent text-[0.875rem] text-ink placeholder:text-subtle focus:outline-none" />
        <button type="submit" disabled={!text.trim() || add.isPending} className="h-10 shrink-0 rounded-full bg-ink px-4 text-[0.8125rem] font-semibold text-surface transition-opacity disabled:opacity-40">
          + Add note
        </button>
      </form>
    </div>
  );
}

// ------------------------------------------------------------------ files

function FilePreview({ f }: { f: ProjectFile }) {
  const { url } = useBlobUrl(f.previewKind === 'image' ? `/api/attachments/${f.id}/preview` : null);
  if (url) return <img src={url} alt="" className="h-full w-full object-cover" />;
  // A paper-like placeholder with faint lines, so documents read as documents.
  return (
    <div className="flex h-full w-full flex-col gap-1.5 bg-white p-3 text-slate-300" aria-hidden>
      <div className="mb-1 h-2 w-1/2 rounded bg-slate-300" />
      {[1, 0.9, 1, 0.7, 1, 0.8].map((w, i) => (
        <div key={i} className="h-1.5 rounded bg-slate-200" style={{ width: `${w * 100}%` }} />
      ))}
      <FileText className="mt-auto h-5 w-5 self-end text-slate-400" />
    </div>
  );
}

function ProjectFiles({ p }: { p: ProjectSummary }) {
  const files = useProjectFiles(p.id);
  const navigate = useNavigate();
  const toast = useToast();
  const list = files.data?.files ?? [];
  return (
    <section>
      {/* 40 px tall, like the tab bar beside it, so the first card lines up with the panel's top edge. */}
      <h2 className="flex h-10 items-center justify-between text-[0.9375rem] font-semibold text-ink">
        Files {!files.isLoading && <span className="mp-pill mp-pill-neutral !h-5">{list.length}</span>}
      </h2>
      {files.isLoading ? (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}</div>
      ) : list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line px-4 py-8 text-center text-xs text-muted">Attachments from this project's conversations will appear here.</div>
      ) : (
        <ul className="space-y-3">
          {list.slice(0, 8).map((f) => (
            <li key={f.id} className="mp-panel overflow-hidden !rounded-2xl p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[0.6875rem] text-subtle">{listTime(f.at)}</p>
                  <p className="truncate text-[0.8125rem] font-semibold text-ink" title={f.filename}>{f.filename}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <RoundButton label="Open conversation" icon={<Pin />} onClick={() => navigate(`/projects/${p.id}/${f.threadId}`)} className="!h-8 !w-8" />
                  <RoundButton label="Download" icon={<Download />} onClick={() => downloadFile(`/api/attachments/${f.id}/download`, f.filename).catch((e) => toast.error(e))} className="!h-8 !w-8" />
                </div>
              </div>
              <button type="button" onClick={() => downloadFile(`/api/attachments/${f.id}/download`, f.filename).catch((e) => toast.error(e))} className="block h-36 w-full overflow-hidden rounded-xl border border-line/60 bg-sunken" aria-label={`Download ${f.filename}`}>
                <FilePreview f={f} />
              </button>
              <p className="mt-2 truncate text-[0.6875rem] text-muted">{f.from} · {bytes(f.size)}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ senders, members, settings

function ProjectSenders({ p }: { p: ProjectSummary }) {
  const [rules, setRules] = useState<{ kind: ProjectRuleKind; value: string }[]>(p.rules.map((r) => ({ kind: r.kind, value: r.value })));
  const save = useSetProjectRules(p.id);
  const toast = useToast();
  const dirty = JSON.stringify(rules) !== JSON.stringify(p.rules.map((r) => ({ kind: r.kind, value: r.value })));
  return (
    <div className="p-5 md:p-6">
      <h3 className="text-[0.9375rem] font-semibold text-ink">Which mail goes in</h3>
      <p className="mt-1 text-[0.8125rem] text-muted">Conversations from these senders, anyone at these domains, or with these phrases in the subject. Click a label to switch between sender, domain and subject.</p>
      <div className="mt-4 space-y-4">
        {p.canManage ? <RulesEditor rules={rules} onChange={setRules} /> : (
          <ul className="flex flex-wrap gap-1.5">{p.rules.map((r) => <li key={r.id} className="mp-pill mp-pill-neutral !h-7">{r.value}</li>)}</ul>
        )}
        {p.canManage && (
          <div className="flex justify-end">
            <Button variant="primary" className="!rounded-full" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate(rules, { onSuccess: (r) => toast.success(r.added ? `Saved — ${r.added} matching conversation${r.added === 1 ? '' : 's'} added.` : 'Saved.') })}>
              Save rules
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectMembers({ p }: { p: ProjectSummary }) {
  const people = useWorkspaceDetail().data?.members ?? [];
  const me = useMe().data;
  const [value, setValue] = useState(p.members.map((m) => ({ userId: m.userId, role: m.role })));
  const save = useSetProjectMembers(p.id);
  return (
    <div className="p-5 md:p-6">
      <h3 className="text-[0.9375rem] font-semibold text-ink">Who follows up</h3>
      <p className="mt-1 text-[0.8125rem] text-muted">Members see every conversation in this project, even in mailboxes their division can't otherwise see. Managers can also change the project.</p>
      <div className="mt-4 space-y-4">
        {p.canManage ? (
          <>
            <MemberPicker people={people} value={value} onChange={setValue} />
            <div className="flex justify-end">
              <Button variant="primary" className="!rounded-full" loading={save.isPending} onClick={() => save.mutate(value)}>Save members</Button>
            </div>
          </>
        ) : (
          <ul className="divide-y divide-line/60">
            {p.members.map((m) => (
              <li key={m.userId} className="flex items-center gap-3 py-2">
                <Avatar name={m.name} email={m.email} size={28} />
                <span className="flex-1 text-[0.8125rem] text-ink">{m.name}{m.userId === me?.user.id ? ' (you)' : ''}</span>
                <span className="mp-pill mp-pill-neutral">{m.role === 'manager' ? 'Manager' : 'Member'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ProjectSettings({ p }: { p: ProjectSummary }) {
  const { isAdmin } = useCurrentWorkspace();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const update = useUpdateProject(p.id);
  const del = useDeleteProject(p.id);
  const prefs = useProjectPrefs(p.id);
  const [name, setName] = useState(p.name);
  const [description, setDescription] = useState(p.description);
  const [cover, setCover] = useState<ProjectCover>(p.cover);
  const tryTone = async (t: NotificationTone) => {
    await unlockAudio();
    playTone(t, 0.5);
  };
  return (
    <div className="space-y-6 p-5 md:p-6">
      {p.me && (
        <section>
          <h3 className="flex items-center gap-2 text-[0.9375rem] font-semibold text-ink">{p.me.notify ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />} Your notifications</h3>
          <p className="mt-1 text-[0.8125rem] text-muted">Just for you: whether new mail in this project notifies you, and the sound it makes.</p>
          <div className="mt-4 space-y-4">
            <Switch checked={p.me.notify} onChange={(v) => prefs.mutate({ notify: v })} label="Notify me about new mail in this project" />
            <div className="flex flex-wrap gap-2">
              {NOTIFICATION_TONES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  aria-pressed={p.me!.tone === t.id}
                  onClick={() => {
                    prefs.mutate({ tone: t.id });
                    void tryTone(t.id);
                  }}
                  className={clsx('mp-pill !h-9 !px-4 !text-[0.8125rem] transition-transform hover:scale-[1.03]', p.me!.tone === t.id ? 'mp-pill-lime' : 'mp-pill-neutral')}
                >
                  {t.id !== 'none' && <Play className="h-3.5 w-3.5" aria-hidden />} {t.label}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}
      {p.canManage && (
        <section className="border-t border-line/70 pt-6">
          <h3 className="flex items-center gap-2 text-[0.9375rem] font-semibold text-ink"><Settings2 className="h-4 w-4" /> Project</h3>
          <div className="mt-4 space-y-4">
            <Field label="Name">{(id) => <Input id={id} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />}</Field>
            <Field label="Description">{(id) => <Input id={id} value={description} maxLength={1000} onChange={(e) => setDescription(e.target.value)} />}</Field>
            <Field label="Cover">
              {() => (
                <CoverPicker
                  value={cover}
                  onChange={setCover}
                  onUpload={(f) =>
                    void uploadProjectCover(p.id, f)
                      .then((c) => {
                        setCover(c);
                        toast.success('Cover updated.');
                        update.mutate({});
                      })
                      .catch((e) => toast.error(e))
                  }
                />
              )}
            </Field>
            <div className="flex justify-end">
              <Button variant="primary" className="!rounded-full" loading={update.isPending} onClick={() => update.mutate({ name: name.trim(), description, ...(cover.kind === 'gradient' ? { cover } : {}) })} disabled={!name.trim()}>
                Save
              </Button>
            </div>
          </div>
        </section>
      )}
      {isAdmin && (
        <section className="flex flex-wrap items-center justify-between gap-3 border-t border-line/70 pt-6">
          <div>
            <p className="text-[0.875rem] font-medium text-ink">Delete project</p>
            <p className="text-xs text-muted">The conversations stay in your inbox; only the project and its rules are removed.</p>
          </div>
          <Button
            variant="danger"
            className="!rounded-full"
            icon={<Trash2 className="h-4 w-4" />}
            onClick={async () => {
              if (await confirm({ title: `Delete ${p.name}?`, message: 'This removes the project, its rules and members. No email is deleted.', confirmLabel: 'Delete project', danger: true })) del.mutate(undefined, { onSuccess: () => navigate('/projects') });
            }}
          >
            Delete
          </Button>
        </section>
      )}
    </div>
  );
}

// Keep the rich card imports referenced for the folder-grid skeletons.
void CardBody;
void CardHeader;
void ProjectCoverView;
void MoreHorizontal;
void Sparkles;
void ThreadRow;
void useCategoryMap;
void useUpdateThread;
